import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";

import ConfirmModal from "../../components/ConfirmModal";
import * as Fonts from "../../components/Fonts";
import { KeepoLoader } from "../../components/KeepoLoader";
import { ScreenHeader } from "../../components/SubHeader";
import { GlobeIcon } from "../../icons/Globe";
import TrashIcon from "../../icons/Trash";
import { isApiError } from "../../services/api/errors";
import { DNS_TYPES, DnsRecord, DnsRecordType, DomainOwner, getDomain, OwnedDomain } from "../../services/domains";
import { formatDate as formatLocaleDate } from "../../services/i18ln/format";
import { formatMoney } from "../../utils/money";
import { classNames } from "../../utils/styles";
import { Field } from "../Subscription/pages/Checkout/BillingDetails";
import { LockIcon } from "../Subscription/pages/Checkout/icons";
import { FieldGrid } from "../Subscription/pages/Checkout/styles";
import { StatusCard } from "../Subscription/pages/Detail/styles";
import { Panel } from "../Subscription/pages/Manage/styles";
import { ConnectKeepoModal } from "./ConnectKeepoModal";
import { useDomainDns } from "./hooks/useDomainDns";
import { useKeepoConnection } from "./hooks/useKeepoConnection";
import { EmailForwarding, Owner, Security } from "./Sections";
import { Container, Note, Records, Section, Segmented, SettingRow, Skeleton, SmallButton, Tag } from "./styles";

const PencilIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round">
    <path d="M4 20h4L19 9l-4-4L4 16v4ZM13.5 6.5l4 4" />
  </svg>
);

const TTL_OPTIONS: DnsRecord["ttl"][] = ["auto", 300, 1800, 3600, 86400];
const STATUS_TONE = { active: "plus", expiring: "warning", expired: "danger", pending: "neutral" } as const;

type Detail = OwnedDomain & { owner?: Partial<DomainOwner> };

/**
 * A domain of the person's: its status and expiry, the Keepo it points to, its DNS, e-mail forwarding,
 * security and owner. While a Keepo is connected, the records that point the domain to it are Keepo's (locked);
 * the rest are the person's. Nameservers of their own hand the whole DNS to another provider.
 */
const DomainDetailPage = () => {
  const { t } = useTranslation();
  const { domain = "" } = useParams();
  // undefined: loading; null: not one of theirs
  const [owned, setOwned] = useState<Detail | null>();
  const [hasLoadError, setLoadError] = useState(false);

  const load = () => {
    setLoadError(false);
    getDomain(domain)
      .then(setOwned)
      // Only "not found" means it isn't theirs: a server or network failure is a retry, not a redirect
      .catch(error => (isApiError(error, "DOMAIN_NOT_FOUND") ? setOwned(null) : setLoadError(true)));
  };

  useEffect(load, [domain]);

  if (owned === null) {
    return <Navigate to="/domains/mine" replace />;
  }
  if (hasLoadError) {
    return (
      <Container>
        <ScreenHeader title={domain} back={{ label: t("pages.domains.mine.title"), to: "/domains/mine" }} />
        <Section>
          <Note role="alert">{t("pages.domains.detail.loadError")}</Note>
          <SmallButton type="button" $primary style={{ alignSelf: "flex-start" }} onClick={load}>
            {t("pages.domains.mine.retry")}
          </SmallButton>
        </Section>
      </Container>
    );
  }
  // Loading: the screen at once (its header, from the address) and its shape pulsing, as on the other pages
  if (!owned) {
    return (
      <Container aria-busy="true">
        <ScreenHeader title={domain} back={{ label: t("pages.domains.mine.title"), to: "/domains/mine" }} />
        <Section>
          <Skeleton className="tall block" />
        </Section>
        {[0, 1, 2].map(section => (
          <Section key={section}>
            <Skeleton className="label" />
            <Skeleton className="block" />
          </Section>
        ))}
      </Container>
    );
  }
  return <DomainDetail owned={owned} setOwned={setOwned} />;
};

const DomainDetail = ({
  owned,
  setOwned,
}: {
  owned: Detail;
  setOwned: Dispatch<SetStateAction<Detail | null | undefined>>;
}) => {
  const { t, i18n } = useTranslation();
  const prefix = "pages.domains.detail";
  const { domain } = owned;

  const [isSaving, setSaving] = useState(false);

  const isPending = owned.status === "pending";

  const {
    dns,
    dnsError,
    showDns,
    records,
    managed,
    ownRecords,
    nsMode,
    setNsMode,
    nameservers,
    setNameservers,
    nameserversError,
    isSwitchOpen,
    setSwitchOpen,
    draft,
    setDraft,
    draftError,
    removing,
    setRemoving,
    startDraft,
    cancelDraft,
    saveRecords,
    saveDraft,
    saveNameservers,
  } = useDomainDns({ domain, isPending, setSaving });
  const {
    keepos,
    keepoId,
    keepo,
    isChoosingKeepo,
    setChoosingKeepo,
    isKeepoRefreshing,
    isDisconnectOpen,
    setDisconnectOpen,
    keepoError,
    setKeepoError,
    changeKeepo,
  } = useKeepoConnection({ owned, setOwned, isPending, showDns, setSaving });

  const formatDate = (date?: string | null) => (date ? formatLocaleDate(date, i18n.language) : "");
  const hostLabel = (host: string) => (host === "@" ? domain : `${host}.${domain}`);

  const recordForm = draft && (
    <li className="editing" key={draft.id || "new"}>
      <FieldGrid>
        <Field label={t(`${prefix}.dns.form.type`)}>
          <select value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value as DnsRecordType })}>
            {DNS_TYPES.map(type => (
              <option key={type} value={type}>
                {t(`${prefix}.dns.types.${type}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t(`${prefix}.dns.form.ttl`)}>
          <select
            value={String(draft.ttl)}
            onChange={e => setDraft({ ...draft, ttl: e.target.value === "auto" ? "auto" : Number(e.target.value) })}>
            {TTL_OPTIONS.map(ttl => (
              <option key={ttl} value={String(ttl)}>
                {t(`${prefix}.dns.ttl.${ttl}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t(`${prefix}.dns.form.host`)} hint={t(`${prefix}.dns.form.hostHint`, { domain })} full>
          <input
            autoFocus
            placeholder="@"
            value={draft.host}
            onChange={e => setDraft({ ...draft, host: e.target.value })}
          />
        </Field>
        <Field
          label={t(`${prefix}.dns.form.value`)}
          error={draftError === "value" ? t(`${prefix}.dns.errors.value`) : undefined}
          full={draft.type !== "MX"}
          fullOnMobile>
          <input
            placeholder={t(`${prefix}.dns.placeholders.${draft.type}`)}
            value={draft.value}
            onChange={e => setDraft({ ...draft, value: e.target.value })}
          />
        </Field>
        {draft.type === "MX" && (
          <Field label={t(`${prefix}.dns.form.priority`)} fullOnMobile>
            <input
              inputMode="numeric"
              value={draft.priority ?? ""}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, "");
                // 0 is a priority too (the best): only an empty field means none
                setDraft({ ...draft, priority: digits === "" ? undefined : Number(digits) });
              }}
            />
          </Field>
        )}
      </FieldGrid>
      {draftError && draftError !== "value" && (
        <p className="form-error" role="alert">
          {t(`${prefix}.dns.errors.${draftError}`)}
        </p>
      )}
      <div className="form-actions">
        <SmallButton type="button" disabled={isSaving} onClick={cancelDraft}>
          {t(`${prefix}.cancel`)}
        </SmallButton>
        <SmallButton type="button" $primary disabled={isSaving} $loading={isSaving} onClick={saveDraft}>
          {t(`${prefix}.dns.form.save`)}
        </SmallButton>
      </div>
    </li>
  );

  return (
    <Container>
      <ScreenHeader title={domain} back={{ label: t("pages.domains.mine.title"), to: "/domains/mine" }} />

      <Section>
        <StatusCard $canceled={owned.status !== "active"}>
          <div className="top">
            <GlobeIcon size={18} />
            <h3 title={domain}>{domain}</h3>
            <Tag $tone={STATUS_TONE[owned.status]} style={{ marginLeft: "auto" }}>
              {t(`pages.domains.status.${owned.status}`)}
            </Tag>
          </div>
          <p className="meta">
            {isPending
              ? t(`${prefix}.overview.pending`)
              : t(`${prefix}.overview.validUntil`, { date: formatDate(owned.expiresAt) })}
            {!isPending &&
              !!owned.renewPrice &&
              ` · ${t(`${prefix}.overview.renewal`, { value: formatMoney(Math.round(owned.renewPrice * 100), "brl", i18n.language) })}`}
          </p>
        </StatusCard>
      </Section>

      {!isPending && (
        <>
          <Section>
            <Fonts.Label>{t(`${prefix}.keepo.title`)}</Fonts.Label>
            {isKeepoRefreshing ? (
              <Records aria-busy="true">
                <li className="empty">
                  <KeepoLoader size={24} color="var(--color-neutral-white)" />
                </li>
              </Records>
            ) : keepo ? (
              <SettingRow>
                <span className="text">
                  <b>{keepo.title || keepo.url}</b>
                  <span>{t(`${prefix}.keepo.connected`, { domain })}</span>
                </span>
                <span className="actions">
                  <SmallButton type="button" onClick={() => setChoosingKeepo(true)}>
                    {t(`${prefix}.keepo.change`)}
                  </SmallButton>
                  <SmallButton type="button" $danger onClick={() => setDisconnectOpen(true)}>
                    {t(`${prefix}.keepo.disconnect`)}
                  </SmallButton>
                </span>
              </SettingRow>
            ) : (
              <SettingRow>
                <span className="text">
                  <b>{t(`${prefix}.keepo.none`)}</b>
                  <span>{t(`${prefix}.keepo.noneHint`)}</span>
                </span>
                <SmallButton type="button" $primary onClick={() => setChoosingKeepo(true)}>
                  {t(`${prefix}.keepo.connect`)}
                </SmallButton>
              </SettingRow>
            )}
            {keepoError && !isChoosingKeepo && (
              <Note className="warning">{t(`${prefix}.keepo.errors.${keepoError}`)}</Note>
            )}
            {/* Connecting or changing: the PLUS Keepos in a modal, with Cancel and Connect at the bottom */}
            {isChoosingKeepo && (
              <ConnectKeepoModal
                domain={domain}
                keepos={keepos.filter(item => item.plus)}
                current={keepoId}
                isSaving={isSaving}
                error={keepoError && t(`${prefix}.keepo.errors.${keepoError}`)}
                onConnect={changeKeepo}
                onClose={() => {
                  setChoosingKeepo(false);
                  setKeepoError(undefined);
                }}
              />
            )}
            {dns?.mode === "custom" && keepo && <Note className="warning">{t(`${prefix}.keepo.customNs`)}</Note>}
          </Section>

          <Section>
            <Fonts.Label>{t(`${prefix}.dns.title`)}</Fonts.Label>
            {/* Loading or failed: inside the box, where the records go */}
            {dnsError || !dns ? (
              <Records aria-busy={!dnsError}>
                <li className={classNames("empty", { error: dnsError })}>
                  {dnsError ? t(`${prefix}.loadError`) : <KeepoLoader size={24} color="var(--color-neutral-white)" />}
                </li>
              </Records>
            ) : (
              <>
                <Segmented role="radiogroup" aria-label={t(`${prefix}.dns.title`)}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={nsMode === "keepo"}
                    disabled={isSaving}
                    onClick={() => (dns.mode === "custom" ? setSwitchOpen(true) : setNsMode("keepo"))}>
                    {t(`${prefix}.dns.mode.keepo`)}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={nsMode === "custom"}
                    disabled={isSaving}
                    onClick={() => setNsMode("custom")}>
                    {t(`${prefix}.dns.mode.custom`)}
                  </button>
                </Segmented>

                {nsMode === "custom" ? (
                  // The form groups' card (as the owner's), the explanation on top
                  <Panel>
                    <p className="intro">{t(`${prefix}.dns.custom.text`)}</p>
                    <FieldGrid>
                      {nameservers.map((nameserver, index) => (
                        <Field key={index} label={t(`${prefix}.dns.custom.label`, { number: index + 1 })} fullOnMobile>
                          <input
                            placeholder={`ns${index + 1}.exemplo.com`}
                            value={nameserver}
                            onChange={e =>
                              setNameservers(nameservers.map((item, i) => (i === index ? e.target.value : item)))
                            }
                          />
                        </Field>
                      ))}
                    </FieldGrid>
                    {nameserversError && (
                      <Note className="warning" role="alert">
                        {t(`${prefix}.dns.custom.${nameserversError === "invalid" ? "invalid" : "error"}`)}
                      </Note>
                    )}
                    <span style={{ display: "flex", gap: "0.5rem" }}>
                      <SmallButton type="button" onClick={() => setNameservers([...nameservers, ""])}>
                        {t(`${prefix}.dns.custom.add`)}
                      </SmallButton>
                      <SmallButton
                        type="button"
                        $primary
                        disabled={isSaving || nameservers.filter(item => item.trim()).length < 2}
                        $loading={isSaving}
                        onClick={() => saveNameservers(nameservers.filter(item => item.trim()))}>
                        {t(`${prefix}.dns.custom.save`)}
                      </SmallButton>
                    </span>
                  </Panel>
                ) : (
                  <>
                    <Records>
                      {managed.map(record => (
                        <li key={record.id}>
                          <span className="type">{record.type}</span>
                          <span className="record">
                            <span className="host">{hostLabel(record.host)}</span>
                            <span className="value">{record.value}</span>
                          </span>
                          <span className="managed" title={t(`${prefix}.dns.managedHint`)}>
                            <LockIcon width={11} height={11} />
                            Keepo
                          </span>
                        </li>
                      ))}
                      {ownRecords.map(record =>
                        draft?.id === record.id ? (
                          recordForm
                        ) : (
                          <li key={record.id}>
                            <span className="type">{record.type}</span>
                            <span className="record">
                              <span className="host">{hostLabel(record.host)}</span>
                              <span className="value">
                                {record.priority !== undefined && record.priority !== null && (
                                  <small>{record.priority}</small>
                                )}
                                {record.value}
                              </span>
                            </span>
                            <span className="row-actions">
                              <button
                                type="button"
                                aria-label={t(`${prefix}.dns.edit`)}
                                onClick={() => startDraft(record)}>
                                <PencilIcon />
                              </button>
                              <button
                                type="button"
                                aria-label={t(`${prefix}.dns.remove`)}
                                onClick={() => setRemoving(record)}>
                                <TrashIcon size={16} color="currentColor" />
                              </button>
                            </span>
                          </li>
                        )
                      )}
                      {draft && !draft.id && recordForm}
                      {!records.length && !draft && <li className="empty">{t(`${prefix}.dns.empty`)}</li>}
                    </Records>
                    {!draft && (
                      <SmallButton type="button" style={{ alignSelf: "flex-start" }} onClick={() => startDraft()}>
                        + {t(`${prefix}.dns.add`)}
                      </SmallButton>
                    )}
                    {!draft && draftError && draftError !== "value" && (
                      <Note className="warning" role="alert">
                        {t(`${prefix}.dns.errors.${draftError}`)}
                      </Note>
                    )}
                    <Note>{t(`${prefix}.dns.propagation`)}</Note>
                  </>
                )}
              </>
            )}
          </Section>

          <EmailForwarding
            domain={domain}
            blockedBy={
              dns?.mode === "custom" ? "nameservers" : records.some(item => item.type === "MX") ? "mx" : undefined
            }
          />
          <Security owned={owned} onChange={next => setOwned(current => current && { ...current, ...next })} />
          <Owner
            domain={domain}
            owner={owned.owner}
            onChange={next => setOwned(current => current && { ...current, ...next })}
          />
        </>
      )}

      {isDisconnectOpen && keepo && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.keepo.disconnectModal.title`)}
          descriptions={[t(`${prefix}.keepo.disconnectModal.text`, { keepo: keepo.title || keepo.url, domain })]}
          confirmButtonTitle={t(`${prefix}.keepo.disconnect`)}
          cancelTitle={t(`${prefix}.cancel`)}
          confirming={isSaving}
          onConfirm={async () => {
            await changeKeepo(null);
            setDisconnectOpen(false);
          }}
          onCancel={() => setDisconnectOpen(false)}
        />
      )}

      {isSwitchOpen && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.dns.switchModal.title`)}
          descriptions={[t(`${prefix}.dns.switchModal.text`)]}
          confirmButtonTitle={t(`${prefix}.dns.switchModal.confirm`)}
          cancelTitle={t(`${prefix}.cancel`)}
          confirming={isSaving}
          onConfirm={async () => {
            await saveNameservers([]);
            setSwitchOpen(false);
          }}
          onCancel={() => setSwitchOpen(false)}
        />
      )}

      {removing && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.dns.removeModal.title`)}
          descriptions={[t(`${prefix}.dns.removeModal.text`, { type: removing.type, host: hostLabel(removing.host) })]}
          confirmButtonTitle={t(`${prefix}.dns.remove`)}
          cancelTitle={t(`${prefix}.cancel`)}
          confirming={isSaving}
          onConfirm={async () => {
            await saveRecords(ownRecords.filter(item => item.id !== removing.id));
            setRemoving(undefined);
          }}
          onCancel={() => setRemoving(undefined)}
        />
      )}
    </Container>
  );
};

export default DomainDetailPage;
